import {useCallback, useMemo, useReducer} from 'react';
import {TABLE_PER_PAGE_DEFAULT_NO_ROW} from '../../../../../constants/constants';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';

export type SelectedListType = 'PROJECT' | 'WORKSPACE' | 'EVIDENCE_SOURCE';
interface SelectionListItemType {
  id: string;
  name: string;
  code?: string;
  parentId?: string;
  custodian?: string;
}

export interface State {
  selectedListType: SelectedListType | null;
  evidenceSourceList: SelectionListItemType[];
  selectedProject?: SelectionListItemType;
  selectedWorkspace?: SelectionListItemType;
  selectedEvidenceSource?: SelectionListItemType;
  searchQuery: string;
  currentPageNo: number;
  noOfItemPerPage: number;
  evidenceLoading: boolean;
  evidenceReqError: object | null;
  totalEvidenceSources: number;
  evidenceSearchLoading: boolean;
}

export enum ActionType {
  SET_SELECTED_LIST_TYPE = 'SET_SELECTED_LIST_TYPE',
  STORE_PROJECT_RESPONSE = 'STORE_PROJECT_RESPONSE',
  START_PROJECT_LOADING = 'START_PROJECT_LOADING',
  SET_PROJECT_REQUEST_ERROR = 'SET_PROJECT_REQUEST_ERROR',
  SET_SELECTED_PROJECT = 'SET_SELECTED_PROJECT',
  SET_SELECTED_WORKSPACE = 'SET_SELECTED_WORKSPACE',
  SET_SELECTED_EVIDENCE = 'SET_SELECTED_EVIDENCE',
  SET_SEARCH_TERM = 'SET_SEARCH_TERM',
  SET_CURRENT_PAGE_NO = 'SET_CURRENT_PAGE_NO',
  SET_NO_OF_ITEM_PAGE_PAGE = 'SET_NO_OF_ITEM_PAGE_PAGE',
  SET_INITIAL_DATA = 'SET_INITIAL_DATA',
  START_EVIDENCE_LOADING = 'START_EVIDENCE_LOADING',
  STORE_EVIDENCE_RESPONSE = 'STORE_EVIDENCE_RESPONSE',
  SET_EVIDENCE_REQUEST_ERROR = 'SET_EVIDENCE_REQUEST_ERROR',
  SET_EVIDENCE_SEARCH_LOADING = 'SET_EVIDENCE_SEARCH_LOADING',
  RESET_DATA_ON_CANCEL = 'RESET_DATA_ON_CANCEL',
}

export const defaultState: State = {
  selectedListType: null,
  evidenceSourceList: [],
  searchQuery: '',
  currentPageNo: 1,
  noOfItemPerPage: TABLE_PER_PAGE_DEFAULT_NO_ROW,
  evidenceLoading: false,
  evidenceReqError: null,
  totalEvidenceSources: 0,
  evidenceSearchLoading: false,
};

type ActionDataType = any;

export const reducer = (
  state: State,
  action: {type: ActionType; data: ActionDataType}
): State => {
  switch (action.type) {
    case ActionType.SET_INITIAL_DATA: {
      const {
        projectName,
        projectId,
        workspaceName,
        workspaceId,
        mediaEvidenceId,
        mediaEvidenceName,
      } = action.data;
      const selectedProject: SelectionListItemType = {
        id: projectId,
        name: projectName,
      };
      const selectedWorkspace: SelectionListItemType = {
        id: workspaceId,
        name: workspaceName,
        parentId: projectId,
      };

      let selectedEvidence = state.selectedEvidenceSource;
      if (mediaEvidenceId && mediaEvidenceName) {
        selectedEvidence = {id: mediaEvidenceId, name: mediaEvidenceName};
      }

      return {
        ...state,
        selectedProject: selectedProject,
        selectedWorkspace: selectedWorkspace,
        selectedEvidenceSource: selectedEvidence,
        selectedListType: workspaceId ? 'EVIDENCE_SOURCE' : 'WORKSPACE',
      };
    }
    case ActionType.SET_SELECTED_LIST_TYPE: {
      let searchQuery = state.searchQuery;
      let currentPageNo = state.currentPageNo;
      let noOfItemPerPage = state.noOfItemPerPage;
      // clear searchQuery and rest pagination number when list type get changed
      if (action.data !== state.selectedListType) {
        searchQuery = '';
        currentPageNo = 1;
        noOfItemPerPage = TABLE_PER_PAGE_DEFAULT_NO_ROW;
      }

      return {
        ...state,
        selectedListType: action.data as SelectedListType,
        searchQuery: searchQuery,
        currentPageNo: currentPageNo,
        noOfItemPerPage: noOfItemPerPage,
      };
    }
    case ActionType.SET_NO_OF_ITEM_PAGE_PAGE: {
      return {
        ...state,
        noOfItemPerPage: action.data,
        currentPageNo: 1,
      };
    }
    case ActionType.SET_CURRENT_PAGE_NO: {
      return {
        ...state,
        currentPageNo: action.data,
      };
    }
    case ActionType.SET_SELECTED_PROJECT: {
      const selectedProject = action.data as SelectionListItemType;
      return {
        ...state,
        selectedProject: selectedProject,
        selectedListType: 'WORKSPACE',
        // clear searchQuery and rest pagination number when list type get changed
        searchQuery: '',
        noOfItemPerPage: TABLE_PER_PAGE_DEFAULT_NO_ROW,
        currentPageNo: 1,
        selectedWorkspace: undefined,
        selectedEvidenceSource: undefined,
        evidenceSourceList: [],
      };
    }
    case ActionType.SET_SELECTED_WORKSPACE: {
      const selectedWorkspace = action.data as SelectionListItemType;

      return {
        ...state,
        selectedWorkspace: selectedWorkspace,
        selectedListType: 'EVIDENCE_SOURCE',
        // clear searchQuery and rest pagination number when list type get changed
        searchQuery: '',
        noOfItemPerPage: TABLE_PER_PAGE_DEFAULT_NO_ROW,
        currentPageNo: 1,
        selectedEvidenceSource: undefined,
        evidenceSourceList: [],
      };
    }
    case ActionType.STORE_EVIDENCE_RESPONSE: {
      const {evidences, totalItemCount} = action.data;
      const evidenceList: SelectionListItemType[] = evidences.reduce(
        (
          result: SelectionListItemType[],
          item: {evidenceSource: string; mediaId: string; custodian: string}
        ) => {
          result.push({
            id: item.mediaId,
            name: `SAFEID${item?.mediaId?.toString().padStart(5, '0')}`,
            custodian: item.custodian,
          });
          return result;
        },
        []
      );

      return {
        ...state,
        evidenceLoading: false,
        evidenceSearchLoading: false,
        evidenceReqError: null,
        evidenceSourceList: evidenceList,
        totalEvidenceSources: totalItemCount,
      };
    }
    case ActionType.SET_SELECTED_EVIDENCE: {
      return {
        ...state,
        selectedEvidenceSource: action.data as SelectionListItemType,
      };
    }
    case ActionType.SET_EVIDENCE_SEARCH_LOADING: {
      return {
        ...state,
        evidenceLoading: false,
        evidenceReqError: null,
        evidenceSearchLoading: action.data ?? false,
      };
    }
    case ActionType.START_EVIDENCE_LOADING: {
      return {
        ...state,
        evidenceSourceList: [],
        evidenceLoading: action.data ?? false,
        evidenceReqError: null,
      };
    }
    case ActionType.SET_EVIDENCE_REQUEST_ERROR: {
      return {
        ...state,
        evidenceReqError: action.data,
        evidenceLoading: false,
        evidenceSearchLoading: false,
      };
    }
    case ActionType.SET_SEARCH_TERM: {
      return {
        ...state,
        searchQuery: action.data,
        currentPageNo: 1,
      };
    }
    case ActionType.RESET_DATA_ON_CANCEL: {
      return {
        ...state,
        selectedListType: 'EVIDENCE_SOURCE',
        selectedProject: undefined,
        selectedWorkspace: undefined,
        selectedEvidenceSource: undefined,
        evidenceSourceList: [],
      };
    }
    default:
      return state;
  }
};

export const useEvidenceSelectionReducer = () => {
  const [inputData, dispatch] = useReducer(reducer, {...defaultState});
  const {
    selectedListType,
    evidenceSourceList,
    searchQuery,
    selectedProject,
    selectedWorkspace,
    selectedEvidenceSource,
    evidenceReqError,
    evidenceLoading,
    totalEvidenceSources,
    noOfItemPerPage,
    currentPageNo,
  } = inputData;

  const {currentProject} = useSelector((state: RootState) => state.reports);

  const {
    projectList: projects,
    workspaceList,
    projectWorkSpaceMap,
  } = useSelector((state: RootState) => state.dialog);

  const setValue = useCallback(
    (actionType: ActionType, data?: ActionDataType) => {
      dispatch({type: actionType, data});
    },
    []
  );

  const setSelectedListType = useCallback((type: SelectedListType) => {
    dispatch({type: ActionType.SET_SELECTED_LIST_TYPE, data: type});
  }, []);

  const projectList = useMemo(
    () =>
      !currentProject?.isNonConsilioWorkflow
        ? projects?.filter((project: any) => !project?.isNonConsilioWorkflow)
        : projects,
    [currentProject?.isNonConsilioWorkflow, projects]
  );

  const {tableRows, totalRowCount} = useMemo(() => {
    let list: SelectionListItemType[];
    let totalCount = 0;
    switch (selectedListType) {
      case 'PROJECT':
        list = [...projectList];
        totalCount = projectList.length;
        break;
      case 'WORKSPACE':
        if (selectedProject) {
          list = [...(projectWorkSpaceMap[selectedProject.id] ?? [])];
        } else list = [...workspaceList];
        totalCount = list.length;
        break;
      case 'EVIDENCE_SOURCE':
        list = evidenceSourceList;
        totalCount = totalEvidenceSources;
        break;
      default:
        list = [];
    }
    if (searchQuery !== '' && selectedListType !== 'EVIDENCE_SOURCE') {
      list = list.filter((item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      totalCount = list.length;
    }
    if (selectedListType === 'PROJECT' || selectedListType === 'WORKSPACE') {
      const startIndex = (currentPageNo - 1) * noOfItemPerPage;
      list = list.slice(startIndex, startIndex + noOfItemPerPage);
    }
    return {tableRows: list, totalRowCount: totalCount};
  }, [
    selectedListType,
    searchQuery,
    projectList,
    selectedProject,
    workspaceList,
    evidenceSourceList,
    totalEvidenceSources,
    projectWorkSpaceMap,
    currentPageNo,
    noOfItemPerPage,
  ]) as {tableRows: SelectionListItemType[]; totalRowCount: number};

  const selectedRowItem = useMemo(() => {
    switch (selectedListType) {
      case 'PROJECT':
        return selectedProject;
      case 'WORKSPACE':
        return selectedWorkspace;
      case 'EVIDENCE_SOURCE':
        return selectedEvidenceSource;
      default:
        return undefined;
    }
  }, [
    selectedListType,
    selectedWorkspace,
    selectedProject,
    selectedEvidenceSource,
  ]);

  const haveReqError = useMemo(() => {
    if (selectedListType === 'EVIDENCE_SOURCE') {
      return evidenceReqError;
    }
    return null;
  }, [selectedListType, evidenceReqError]);

  const enableContinueButton = useMemo(() => {
    if (selectedProject && selectedWorkspace && selectedEvidenceSource) {
      return true;
    }
    return false;
  }, [selectedProject, selectedWorkspace, selectedEvidenceSource]);

  const reqLoading = useMemo(() => {
    if (selectedListType === 'EVIDENCE_SOURCE') {
      return evidenceLoading;
    }
    return null;
  }, [selectedListType, evidenceLoading]);

  return {
    ...inputData,
    setValue,
    setSelectedListType,
    tableRows,
    selectedRowItem,
    haveReqError,
    reqLoading,
    enableContinueButton,
    totalRowCount,
  };
};
